/**
 * Silo API Error Classes - Typed error handling matching backend formats
 * Based on FRONTEND_API_INTEGRATION_GUIDE.md patterns
 */

// Base API Error
export class APIError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

// Specific Error Types
export class AuthenticationError extends APIError {
  constructor(message: string, details?: any) {
    super(401, 'AUTHENTICATION_ERROR', message, details);
  }
}

export class AuthorizationError extends APIError {
  constructor(message: string, details?: any) {
    super(403, 'AUTHORIZATION_ERROR', message, details);
  }
}

export class ValidationError extends APIError {
  constructor(message: string, public fields: Record<string, string[]>) {
    super(400, 'VALIDATION_ERROR', message, fields);
  }
}

export class NotFoundError extends APIError {
  constructor(resource: string, id?: string | number) {
    super(404, 'NOT_FOUND', `${resource}${id ? ` with id ${id}` : ''} not found`);
  }
}

export class RateLimitError extends APIError {
  constructor(retryAfter?: number) {
    super(429, 'RATE_LIMIT', 'Too many requests', { retryAfter });
  }
}

/**
 * Error Handler Implementation
 * Processes HTTP responses and throws appropriate typed errors
 */
export class ErrorHandler {
  static async handleResponse(response: Response): Promise<any> {
    if (response.ok) {
      // Handle empty responses (204 No Content)
      if (response.status === 204) {
        return null;
      }
      const json = await response.json();
      console.log('[ErrorHandler] Parsed JSON response:', json);
      console.log('[ErrorHandler] Response URL:', response.url);
      console.log('[ErrorHandler] Response status:', response.status);
      return json;
    }

    const contentType = response.headers.get('content-type');
    let errorData: any = {};

    if (contentType?.includes('application/json')) {
      errorData = await response.json();
    }

    switch (response.status) {
      case 400:
        // Validation errors
        if (errorData.fields) {
          throw new ValidationError(
            errorData.message || 'Validation failed',
            errorData.fields
          );
        }
        throw new APIError(400, 'BAD_REQUEST', errorData.detail || 'Bad request', errorData);

      case 401:
        // Authentication errors
        if (errorData.custom_message?.includes('refresh')) {
          throw new AuthenticationError('Token expired', errorData);
        }
        throw new AuthenticationError(errorData.detail || 'Authentication required', errorData);

      case 403:
        // Authorization errors
        throw new AuthorizationError(
          errorData.detail || 'You do not have permission to perform this action',
          errorData
        );

      case 404:
        // Not found errors
        throw new NotFoundError(errorData.detail || 'Resource', errorData.id);

      case 429:
        // Rate limiting
        const retryAfter = response.headers.get('Retry-After');
        throw new RateLimitError(retryAfter ? parseInt(retryAfter) : undefined);

      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors
        throw new APIError(
          response.status,
          'SERVER_ERROR',
          errorData.detail || 'Server error occurred',
          errorData
        );

      default:
        throw new APIError(
          response.status,
          'UNKNOWN_ERROR',
          errorData.detail || `Request failed with status ${response.status}`,
          errorData
        );
    }
  }
}

/**
 * Type guard to check if an error is an API error
 */
export function isAPIError(error: any): error is APIError {
  return error instanceof APIError;
}

/**
 * Type guard to check if an error is a validation error
 */
export function isValidationError(error: any): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Type guard to check if an error is an authentication error
 */
export function isAuthenticationError(error: any): error is AuthenticationError {
  return error instanceof AuthenticationError;
}

/**
 * Type guard to check if an error is a rate limit error
 */
export function isRateLimitError(error: any): error is RateLimitError {
  return error instanceof RateLimitError;
}